<template>
  <div class="travel-route-detail">
    <!-- 顶部导航栏 -->
    <div class="top-nav">
      <div class="container">
        <div class="back-btn" @click="$router.go(-1)">
          <i class="el-icon-arrow-left"></i> {{ $t("back") }}
        </div>
        <div class="share-actions">
          <span class="collect-btn" @click="toggleCollection()">
            <i
              :class="isCollected ? 'el-icon-star-on' : 'el-icon-star-off'"
            ></i>
            {{ isCollected ? $t("collected") : $t("collect") }}
          </span>
          <span @click="shareLink" class="share-btn">
            <i class="el-icon-share"></i> {{ $t("share") }}
          </span>
        </div>
      </div>
    </div>
    <!-- 路线标题和卖点 -->
    <div class="container">
      <div class="route-header">
        <h1 class="route-title">{{ routeData.routeName }}</h1>
        <div class="tags">
          <el-tag
            v-for="(tag, i) in routeData.features"
            :key="i"
            type="danger"
            size="small"
            >{{ tag }}</el-tag
          >
        </div>
      </div>
      <div class="main-content">
        <!-- 左侧内容区 -->
        <div class="content-left">
          <!-- 顶部图片轮播 -->
          <div class="top-section">
            <el-carousel
              ref="carousel"
              :interval="3000"
              height="400px"
              indicator-position="none"
              v-if="routeData.images && routeData.images.length > 0"
              @change="handleCarouselChange"
            >
              <el-carousel-item
                v-for="(image, index) in routeData.images"
                :key="index"
              >
                <img
                  :src="image"
                  class="carousel-img"
                  :alt="$t('routeImage')"
                />
              </el-carousel-item>
            </el-carousel>
            <!-- 小图预览 -->
            <div
              class="thumbnails"
              v-if="routeData.images && routeData.images.length > 0"
            >
              <img
                v-for="(image, index) in routeData.images"
                :key="index"
                :src="image"
                class="thumbnail"
                :class="{ active: activeThumbnail === index }"
                @click="
                  activeThumbnail = index;
                  $refs.carousel.setActiveItem(index);
                "
                @mouseenter="showThumbnail(index)"
                :alt="$t('thumbnail')"
              />
            </div>
          </div>
          <!-- 产品信息 -->
          <div class="product-info card">
            <h3 class="section-title">{{ $t("productInfo") }}</h3>
            <div class="info-grid">
              <div class="info-item">
                <span class="label">{{ $t("productNo") }}：</span>
                <span class="value">{{ routeData.number }}</span>
              </div>
              <div class="info-item">
                <span class="label">{{ $t("tripDays") }}：</span>
                <span class="value">{{ routeData.days }} {{ $t("day") }}</span>
              </div>
              <div class="info-item">
                <span class="label">{{ $t("departDest") }}：</span>
                <span class="value" style="color: #1677ff; font-weight: 500"
                  >{{ routeData.departure }}/{{ routeData.destination }}
                </span>
              </div>
              <div class="info-item">
                <span class="label">{{ $t("travelMode") }}：</span>
                <span class="value">{{ routeData.travelType }}</span>
              </div>
              <div class="info-item">
                <span class="label">{{ $t("recentTrip") }}：</span>
                <span
                  v-if="routeData.startDate"
                  class="value"
                  style="color: #1677ff; font-weight: 500"
                >
                  {{ formatDate(routeData.startDate) }} -
                  {{ formatDate(routeData.endDate) }}
                </span>
                <span
                  v-else
                  class="value"
                  style="color: #1677ff; font-weight: 500"
                  >{{ $t("undetermined") }}</span
                >
              </div>
              <div class="info-item">
                <span class="label">{{ $t("historySales") }}：</span>
                <span class="value"
                  >{{ routeData.historySales }} {{ $t("order") }}</span
                >
              </div>
              <div class="info-item">
                <span class="label">{{ $t("satisfaction") }}：</span>
                <span class="value">{{ routeData.satisfaction }}%</span>
              </div>
              <div class="info-item">
                <span class="label">{{ $t("commentCount") }}：</span>
                <span class="value"
                  >{{ routeData.commentCount }} {{ $t("piece") }}</span
                >
              </div>
            </div>
          </div>
          <!-- 服务承诺 -->
          <div class="service-promises card">
            <h3 class="section-title">{{ $t("servicePromise") }}</h3>
            <div class="promise-list">
              <el-tag
                v-for="(promise, index) in routeData.servicePromises"
                :key="index"
                effect="plain"
                size="small"
              >
                {{ promise }}
              </el-tag>
            </div>
            <!-- 新增服务商提示语 -->
            <div
              class="provider-info"
              style="
                margin-top: 15px;
                padding-top: 15px;
                border-top: 1px dashed #eee;
              "
            >
              <i
                class="el-icon-info"
                style="color: #1677ff; margin-right: 8px"
              ></i>
              <span style="color: #333"
                >{{ $t("serviceProviderTip1") }}
                <strong style="color: #1677ff">{{
                  routeData.providerCompany && routeData.providerCompany.trim()
                    ? routeData.providerCompany
                    : $t("unknownProvider")
                }}</strong>
                {{ $t("serviceProviderTip2") }}</span
              >
            </div>
          </div>
          <!-- 路线简介 -->
          <div class="section card">
            <h3 class="section-title">{{ $t("routeIntro") }}</h3>
            <p class="section-content">{{ routeData.intro }}</p>
          </div>
          <!-- 行程安排（时间线样式） -->
          <div
            class="section card schedule-timeline"
            v-if="routeData.schedule && routeData.schedule.length > 0"
          >
            <h3 class="section-title">{{ $t("tripSchedule") }}</h3>
            <!-- 每天的行程 -->
            <div class="timeline-container">
              <div
                class="day-group"
                v-for="(day, dayIndex) in routeData.schedule"
                :key="dayIndex"
              >
                <!-- 天数标题 -->
                <div class="day-title">
                  <span class="day-badge"
                    >{{ $t("day") }} {{ dayIndex + 1 }}</span
                  >
                </div>
                <!-- 当天景点时间线 -->
                <div class="day-timeline">
                  <!-- 时间线轴线 -->
                  <div class="timeline-line"></div>
                  <!-- 每个景点作为时间线节点 -->
                  <div
                    class="timeline-item"
                    v-for="(spot, spotIndex) in day.spots"
                    :key="spotIndex"
                  >
                    <!-- 时间点标记 -->
                    <div class="timeline-dot">
                      <i class="el-icon-map-marker"></i>
                    </div>
                    <!-- 时间与内容 -->
                    <div class="timeline-content">
                      <div class="spot-time">{{ spot.time }}</div>
                      <div class="spot-content" v-html="spot.content"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- 路线详情 -->
          <div class="section card">
            <h3 class="section-title">{{ $t("routeDetail") }}</h3>
            <div class="detail-content" v-html="routeData.details"></div>
          </div>
          <!-- 费用说明 -->
          <div class="section card" v-if="formatText(routeData.costDesc)">
            <h3 class="section-title">{{ $t("costInfo") }}</h3>
            <div class="cost-content">
              <div class="cost-type">
                {{ routeData.costDesc }}
              </div>
            </div>
          </div>
          <!-- 预订须知 -->
          <div class="section card" v-if="routeData.bookingNotice">
            <h3 class="section-title">{{ $t("bookingNotice") }}</h3>
            <div class="notice-content">
              <p v-html="formatText(routeData.bookingNotice)"></p>
            </div>
          </div>
        </div>
        <!-- 右侧预订区 -->
        <div class="content-right">
          <div ref="bookingContainer" class="booking-card">
            <div class="price-section">
              <div class="price-info">
                <span class="price">¥{{ routeData.price }}</span>
                <span class="unit">{{ $t("perPerson") }}</span>
              </div>
              <div class="rating">
                <div class="stars">
                  <!-- 点亮的星星（取整数部分） -->
                  <i
                    class="el-icon-star-on"
                    v-for="(star, i) in Math.round(
                      Math.min(Math.max(Number(routeData.rating) || 0, 0), 5),
                    )"
                    :key="i"
                  ></i>
                  <!-- 未点亮的星星 -->
                  <i
                    class="el-icon-star-off"
                    v-for="(star, i) in 5 -
                    Math.round(
                      // 同样取整数
                      Math.min(Math.max(Number(routeData.rating) || 0, 0), 5),
                    )"
                    :key="i + 5"
                  ></i>
                  <span class="score"
                    >{{ routeData.rating }}{{ $t("score") }}</span
                  >
                </div>
                <div class="rating-text">
                  {{ $t("basedOn") }}{{ commentCount }}{{ $t("reviews") }}
                </div>
              </div>
            </div>
            <div class="booking-form">
              <div class="form-row">
                <div class="form-group half">
                  <label class="form-label">{{ $t("adult") }}</label>
                  <el-input-number
                    v-model="adultCount"
                    :min="1"
                    @change="handleAdultChange"
                  />
                </div>
                <div class="form-group half">
                  <label class="form-label">{{ $t("room") }}</label>
                  <el-input-number
                    v-model="roomCount"
                    :min="1"
                    :max="adultCount"
                    @change="handleRoomChange"
                  />
                </div>
              </div>
              <div class="package-info">
                <h4>{{ $t("themeRec") }}：</h4>
                <div class="theme">
                  <el-tag
                    v-for="(tag, i) in routeData.themes"
                    :key="i"
                    size="small"
                    >{{ tag }}</el-tag
                  >
                </div>
                <h4>{{ $t("recentDate") }}：</h4>
                <p class="tips">
                  <el-tag v-if="routeData.startDate" type="danger" size="small"
                    >{{ formatDate(routeData.startDate) }} {{ $t("to") }}
                    {{ formatDate(routeData.endDate) }}</el-tag
                  >
                  <span v-else>{{ $t("undetermined") }}</span>
                </p>
                <h4>{{ $t("serviceProvider") }}：</h4>
                <p class="tips">
                  <el-tag
                    v-if="routeData.startDate"
                    type="success"
                    size="small"
                  >
                    {{
                      routeData.providerCompany &&
                      routeData.providerCompany.trim()
                        ? routeData.providerCompany
                        : $t("unknownProvider")
                    }}
                  </el-tag>
                </p>
              </div>
              <div class="total-price">
                <div class="total-label">{{ $t("totalPrice") }}</div>
                <div class="total-value">¥{{ totalPrice }}</div>
              </div>
              <div class="action-buttons">
                <el-button
                  type="primary"
                  class="book-btn"
                  @click="showDatePicker = true"
                  >{{ $t("bookNow") }}</el-button
                >
              </div>
            </div>
          </div>
          <!-- 游客评价 - 使用循环数据 -->
          <div class="reviews-card card">
            <h3 class="section-title">{{ $t("userReviews") }}</h3>
            <div
              class="review-item"
              v-for="(review, index) in reviews"
              :key="index"
            >
              <div class="review-header">
                <div class="user-avatar">
                  <img :src="review.avatar" :alt="$t('avatar')" />
                </div>
                <div class="user-info">
                  <div class="user-name">{{ review.userName }}</div>
                  <div class="review-date">{{ review.date }}</div>
                </div>
                <div class="review-stars">
                  <i
                    class="el-icon-star-on"
                    v-for="(star, i) in review.rating"
                    :key="i"
                  ></i>
                  <i
                    class="el-icon-star-off"
                    v-for="(star, i) in 5 - review.rating"
                    :key="i + review.rating"
                  ></i>
                </div>
              </div>
              <div class="review-content">
                {{ review.content }}
              </div>
              <div
                class="review-images"
                v-if="review.images && review.images.length > 0"
              >
                <img
                  v-for="(img, imgIndex) in review.images"
                  :key="imgIndex"
                  :src="img"
                  :alt="$t('reviewImg')"
                />
              </div>
            </div>
            <el-button
              type="text"
              class="view-more-reviews"
              @click="toggleScroll()"
              >{{ $t("viewAll") }}{{ commentCount
              }}{{ $t("reviews") }}</el-button
            >
          </div>
        </div>
      </div>
    </div>
    <div :class="['floating-booking', { show: isFloating }]">
      <div class="floating-content">
        <div class="floating-item">
          <span class="floating-label">{{ $t("tripDate") }}：</span>
          <span v-if="routeData.startDate" class="floating-date"
            >{{ formatDate(routeData.startDate) }} {{ $t("to") }}
            {{ formatDate(routeData.endDate) }}</span
          >
          <span v-else class="floating-date">{{ $t("undetermined") }}</span>
        </div>
        <div class="floating-item">
          <span class="floating-label">{{ $t("adult") }}：</span>
          <el-input-number
            v-model="adultCount"
            :min="1"
            @change="handleAdultChange"
          />
        </div>
        <div class="floating-item">
          <span class="floating-label">{{ $t("room") }}：</span>
          <el-input-number
            v-model="roomCount"
            :min="1"
            :max="adultCount"
            @change="handleRoomChange"
          />
        </div>
        <div class="floating-price">
          {{ $t("total") }}：
          <span>¥{{ totalPrice }}</span>
          <el-button
            type="primary"
            class="floating-book-btn"
            @click="showDatePicker = true"
            >{{ $t("bookNow") }}</el-button
          >
        </div>
      </div>
    </div>
    <FloatingActionPanel :route-id="routeId" :is-collected.sync="isCollected" />
    <div class="review-drawer-container">
      <el-drawer
        :visible.sync="showReviewList"
        direction="rtl"
        :size="isMobile ? '100%' : '50%'"
        :title="$t('userReviews') + '（ ' + commentCount + ' ）'"
        @opened="initReviewList"
        @close="enableScroll()"
      >
        <div class="review-list" @scroll="handleReviewScroll">
          <div
            class="review-item"
            v-for="(review, reviewIndex) in all_reviews"
            :key="reviewIndex"
          >
            <div class="review-header">
              <div class="user-avatar">
                <img :src="review.avatar" :alt="$t('avatar')" />
              </div>
              <div class="user-info">
                <span class="user-name">{{ review.userName }}</span>
                <span class="review-date">{{ review.date }}</span>
              </div>
              <div class="review-stars">
                <i
                  class="el-icon-star-on"
                  v-for="(star, starIndex) in review.rating"
                  :key="starIndex"
                ></i>
                <i
                  class="el-icon-star-off"
                  v-for="(star, starIndex) in 5 - review.rating"
                  :key="starIndex + review.rating"
                ></i>
              </div>
            </div>
            <div class="review-content">{{ review.content }}</div>
            <div
              class="review-images"
              v-if="review.images && review.images.length > 0"
            >
              <img
                v-for="(img, imgIndex) in review.images"
                :key="imgIndex"
                :src="img"
                :alt="$t('reviewImg')"
                class="review-img"
              />
            </div>
          </div>
          <!-- 加载状态提示 -->
          <div v-if="loading" class="loading-more">{{ $t("loading") }}...</div>
          <div v-if="!hasMore && !loading" class="no-more">
            {{ $t("noMore") }}
          </div>
        </div>
      </el-drawer>
    </div>
    <!-- 日期选择弹窗 -->
    <el-dialog
      :title="$t('selectTravelDate')"
      :visible.sync="showDatePicker"
      :width="isMobile ? '80%' : '30%'"
      :before-close="handleDateDialogClose"
    >
      <div class="date-selection">
        <!-- 日期列表（仅显示可用日期） -->
        <div class="available-dates">
          <div
            class="date-item"
            v-for="date in availableDates"
            :key="date.timestamp"
            :class="{ selected: selectePlanId === date.palnId }"
            @click="
              selectePlanId = date.palnId;
              selectedDate = date.dateString;
            "
          >
            <div class="date-day">
              {{ formatDate(date.dateString, "short") }}
            </div>
            <div class="date-status" v-if="date.status">{{ date.status }}</div>
          </div>
        </div>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="showDatePicker = false">{{
          $t("cancel")
        }}</el-button>
        <el-button type="primary" @click="confirmDate">{{
          $t("confirm")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import FloatingActionPanel from "../../components/FloatingActionPanel.vue";
export default {
  components: {
    FloatingActionPanel,
  },
  name: "TravelRouteDetail",
  data() {
    return {
      routeId: 0,
      // 选中的缩略图索引
      activeThumbnail: 0,
      // 成人数量
      adultCount: 1,
      // 房间数量
      roomCount: 1,
      // 悬浮状态控制 - 当预订卡片不可见时为true
      isFloating: false,
      // 收藏状态
      isCollected: false,
      // 原始预订区域位置记录
      originalPosition: null,
      // 视口高度
      viewportHeight: 0,
      // 游客评价数据
      reviews: [],
      //游客评价数量
      commentCount: 0,
      routeData: {},
      showReviewList: false,
      activeTag: "全部",
      all_reviews: [],
      currentPage: 1, // 当前页码
      pageSize: 10, // 每页条数
      loading: false, // 加载状态
      hasMore: true, // 是否有更多数据
      showDatePicker: false, // 控制日期选择弹窗显示
      selectedDate: "", // 用户选中的日期
      selectePlanId: "", // 用户选中的计划id
      availableDates: [], // 可用日期列表（从接口获取）
      isDateSelected: false, // 标记是否已选择日期
    };
  },
  created() {
    this.routeId = this.$route.query.id;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 2);
    this.fetchData();
    this.getReviews();
    this.initReviewList();
    this.checkCollectionStatus();
    this.fetchAvailableDates(); // 从接口获取可用日期
  },
  computed: {
    // 计算总价
    totalPrice() {
      return this.adultCount * this.routeData.price;
    },
    isMobile() {
      // 判断是否为手机端，可根据实际需求调整判断逻辑，这里简单通过屏幕宽度判断
      return window.innerWidth < 768;
    },
  },
  methods: {
    toggleScroll() {
      this.showReviewList = !this.showReviewList;
      if (this.showReviewList) {
        // 打开时禁用滚动
        this.disableScroll();
      } else {
        // 手动关闭时启用滚动（兜底）
        this.enableScroll();
      }
    },
    // 页面初始化时检查收藏状态
    checkCollectionStatus() {
      this.$get("/collection/check", {
        associated_id: this.routeId,
      }).then((res) => {
        if (res.data.code === 1) {
          this.isCollected = res.data.data.is_collected; // 初始化前端状态
        }
      });
    },
    fetchData() {
      const id = this.$route.query.id; // 获取路由参数中的id
      const lang = this.$i18n.locale || "zh";
      this.$get("/travel/get_travel_detail", {
        route_id: id,
        language: lang,
      }).then((res) => {
        if (res.data.code === 1) {
          this.routeData = res.data.data;
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    getReviews() {
      this.$get("/review/get_new_reviews", {
        route_id: this.routeId,
      }).then((res) => {
        if (res.data.code === 1) {
          this.reviews = res.data.data.reviews;
          this.commentCount = res.data.data.commentCount;
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    // 显示缩略图预览
    showThumbnail(index) {
      this.activeThumbnail = index;
    },
    // 轮播图变化时同步更新缩略图
    handleCarouselChange(index) {
      this.activeThumbnail = index;
    },
    // 滚动监听处理 - 核心逻辑：判断预订卡片是否可见
    handleScroll() {
      if (!this.originalPosition) return;
      // 获取当前滚动位置
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      // 视口高度
      const viewportHeight = window.innerHeight;
      // 计算预订卡片是否在可视区域内
      // 卡片顶部位置 <= 视口底部 且 卡片底部位置 >= 视口顶部，说明卡片可见
      const isVisible = !(
        (
          this.originalPosition.bottom < scrollTop || // 卡片完全在视口上方
          this.originalPosition.top > scrollTop + viewportHeight
        ) // 卡片完全在视口下方
      );
      // 当卡片不可见时显示悬浮框，可见时隐藏
      this.isFloating = !isVisible;
    },
    // 获取可用日期列表（假设接口返回特定日期数据）
    fetchAvailableDates() {
      this.$get("/travel/get_available_dates", {
        route_id: this.routeId,
      }).then((res) => {
        if (res.data.code === 1) {
          // 格式化日期数据（示例结构）
          this.availableDates = res.data.data.map((date) => ({
            palnId: date.plan_id,
            dateString: date.date, // 日期字符串（如：2024-10-25）
            timestamp: new Date(date.date).getTime(), // 时间戳（用于排序/去重）
            // price: date.price, // 该日期的价格（可选）
            status: date.status, // 日期状态（如："可预订"、"仅剩3席"等，可选）
          }));
        } else {
          this.$message.error(this.$t("getDateFailed"));
        }
      });
    },
    // 确认选择日期
    confirmDate() {
      if (!this.selectedDate) {
        this.$message.warning(this.$t("pleaseSelectDate"));
        return;
      }
      // 隐藏弹窗，标记已选择日期，执行原预订逻辑
      this.showDatePicker = false;
      this.isDateSelected = true;
      this.submitOrder(); // 提交订单
    },
    // 实际提交订单的逻辑（原handleBooking内容迁移至此）
    submitOrder() {
      // 验证预订信息（包含日期）
      if (!this.selectedDate || this.adultCount === 0 || this.roomCount === 0) {
        this.$message.error(this.$t("pleaseFillBookingInfo"));
        return;
      }
      // 订单数据中添加用户选择的日期
      const orderData = {
        routeName: this.routeData.routeName,
        departure: this.routeData.departure,
        destination: this.routeData.destination,
        startDate: this.selectedDate, // 选中的出游日期
        endDate: this.calculateEndDate(this.selectedDate), // 计算结束日期（根据行程天数）
        adultCount: this.adultCount,
        roomCount: this.roomCount,
        totalAmount: this.totalPrice,
        providerCompany: this.routeData.providerCompany,
        travelPlanId: this.selectePlanId,
        travelId: this.routeId,
      };
      // 调用创建订单接口（原逻辑不变）
      this.$post("/order/create", {
        orderData: encodeURIComponent(JSON.stringify(orderData)),
        days: this.routeData.days,
      }).then((res) => {
        if (res.data.code === 1) {
          orderData.orderNo = res.data.data.order_no;
          this.$router.push({
            path: "/travel/booking",
            query: {
              orderData: encodeURIComponent(JSON.stringify(orderData)),
              days: this.routeData.days,
              routeName: encodeURIComponent(this.routeData.routeName),
            },
          });
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    // 辅助方法：根据开始日期和行程天数计算结束日期
    calculateEndDate(startDate) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + this.routeData.days - 1); // 减1是因为开始日算第一天
      return this.formatDate(date);
    },
    // 扩展日期格式化方法（支持短格式显示）
    formatDate(dateString, format = "full") {
      if (!dateString) return "";
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      const week = ["日", "一", "二", "三", "四", "五", "六"][date.getDay()];
      // 短格式：月-日 周X（用于日期选择弹窗）
      if (format === "short") {
        return `${month}-${day} 周${week}`;
      }
      // 长格式：年-月-日（默认）
      return `${year}-${month}-${day}`;
    },
    // 关闭日期弹窗时重置选择状态
    handleDateDialogClose(done) {
      this.isDateSelected = false;
      this.selectedDate = "";
      done();
    },
    shareLink() {
      // 获取当前完整路由URL
      const currentUrl =
        window.location.origin + this.$router.currentRoute.fullPath;
      // 使用剪贴板API复制到剪贴板
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          this.$message.success(this.$t("linkCopied"));
        })
        .catch((err) => {
          console.error("复制失败:", err);
          this.$message.error(this.$t("copyFailed"));
        });
    },
    // 更新视口高度和预订卡片位置
    updateElementPositions() {
      this.viewportHeight = window.innerHeight;
      const bookingEl = this.$refs.bookingContainer;
      if (bookingEl) {
        const rect = bookingEl.getBoundingClientRect();
        // 计算相对于文档的绝对位置（包含滚动偏移）
        this.originalPosition = {
          top: rect.top + window.pageYOffset,
          bottom: rect.bottom + window.pageYOffset,
          height: rect.height,
        };
      }
    },
    // 收藏/取消收藏方法
    toggleCollection() {
      this.$post("/collection/toggle", {
        associated_id: this.routeId, // 传递当前路线ID
      }).then((res) => {
        console.log(res.data, "ppp");
        if (res.data.code === 1) {
          this.isCollected = res.data.data.is_collected; // 更新前端收藏状态
          this.$message.success(res.data.msg);
        } else {
          this.$message.error(res.data.msg);
        }
      });
    },
    handleTagClick(tag) {
      this.activeTag = tag;
    },
    initReviewList() {
      this.currentPage = 1;
      this.all_reviews = [];
      this.hasMore = true;
      this.loadReviewData(); // 加载第一页数据
    },
    // 加载评价数据
    async loadReviewData() {
      if (this.loading || !this.hasMore) return;
      this.loading = true;
      try {
        this.$get("/review/list", {
          route_id: this.routeId,
          page: this.currentPage,
          page_size: this.pageSize,
        }).then((res) => {
          if (res.data.code === 1) {
            // 第一页直接替换，后续页追加
            if (this.currentPage === 1) {
              this.all_reviews = res.data.data.list;
            } else {
              this.all_reviews = [...this.all_reviews, ...res.data.data.list];
            }
            // 判断是否还有更多数据
            this.hasMore = res.data.data.list.length >= this.pageSize;
            this.currentPage++;
          } else {
            this.$message.error(res.msg || this.$t("getReviewFailed"));
          }
        });
      } catch (error) {
        this.$message.error(this.$t("networkErrorGetReviewFailed"));
      } finally {
        this.loading = false;
      }
    },
    // 处理滚动事件
    handleReviewScroll(e) {
      const container = e.target;
      // 滚动到底部附近（距离底部20px）时加载下一页
      if (
        container.scrollHeight - container.scrollTop <=
        container.clientHeight + 20
      ) {
        this.loadReviewData();
      }
    },
    // 禁止页面滚动
    disableScroll() {
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
    },
    // 恢复页面滚动
    enableScroll() {
      console.log("oooo");
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
    },
    // 成人数量变化时，确保房间数不超过成人数量
    handleAdultChange(val) {
      // 如果当前房间数大于新的成人数量，自动调整房间数
      if (this.roomCount > val) {
        this.roomCount = val;
        this.$message.warning(
          `${this.$t("roomCannotExceedAdult")}，${this.$t(
            "autoAdjustTo",
          )}${val}${this.$t("room")}`,
        );
      }
    },
    // 房间数量变化时的额外校验（可选）
    handleRoomChange(val) {
      // 双重保险，防止特殊情况导致房间数超过成人数
      if (val > this.adultCount) {
        this.roomCount = this.adultCount;
        this.$message.warning(
          `${this.$t("roomCannotExceedAdult")}（${this.$t("current")}${
            this.adultCount
          }${this.$t("person")}）`,
        );
      }
    },
  },
  watch: {
    $route() {
      this.fetchData();
    },
    selectedDate() {
      this.handleScroll();
    },
    adultCount() {
      this.handleScroll();
    },
    roomCount() {
      this.handleScroll();
    },
    "$i18n.locale"() {
      this.fetchData(); // 详情页自身刷新
    },
  },
  mounted() {
    window.scrollTo(0, 0);
    // 初始化位置信息
    this.$nextTick(() => {
      this.updateElementPositions();
      // 初始判断一次
      this.handleScroll();
      // 绑定滚动事件
      window.addEventListener("scroll", this.handleScroll);
      // 绑定窗口大小变化事件（窗口大小改变时重新计算位置）
      window.addEventListener("resize", this.updateElementPositions);
    });
  },
  beforeDestroy() {
    // 解绑事件
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.updateElementPositions);
  },
};
</script>

<style scoped>
/* 基础样式 */
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}
.card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  padding: 20px;
  margin-bottom: 20px;
}
.section-title {
  font-size: 18px;
  color: #333;
  padding-bottom: 12px;
  margin-bottom: 15px;
  border-bottom: 1px solid #f5f5f5;
  position: relative;
}
.section-title::after {
  content: "";
  position: absolute;
  left: 0;
  bottom: -1px;
  width: 40px;
  height: 2px;
  background-color: #1677ff;
}
/* 顶部导航 */
.top-nav {
  background-color: #fff;
  border-bottom: 1px solid #eee;
  padding: 12px 0;
  position: sticky;
  top: 0;
  z-index: 10;
}
.top-nav .container {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.back-btn {
  color: #333;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 14px;
}
.back-btn i {
  margin-right: 5px;
}
.share-actions {
  display: flex;
  gap: 15px;
}
.collect-btn,
.share-btn {
  display: flex;
  align-items: center;
  color: #666;
  font-size: 14px;
  cursor: pointer;
}
.collect-btn i,
.share-btn i {
  margin-right: 5px;
}
.collect-btn .el-icon-star-on {
  color: #ff4d4f;
  font-size: large;
}
/* 路线标题和卖点 */
.route-header {
  padding: 20px 0;
}
.route-title {
  font-size: 24px;
  color: #333;
  margin-bottom: 15px;
  line-height: 1.4;
}
.tags {
  margin-bottom: 15px;
  display: flex;
  gap: 10px;
}
/* 顶部图片轮播 */
.top-section {
  margin-bottom: 25px;
}
.el-carousel {
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 12px;
}
.carousel-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
/* 缩略图 */
.thumbnails {
  display: flex;
  gap: 10px;
  justify-content: center;
}
.thumbnail {
  width: 100px;
  height: 70px;
  object-fit: cover;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.3s;
  border: 2px solid transparent;
}
.thumbnail.active {
  border-color: #1677ff;
}
.thumbnail:hover {
  transform: scale(1.05);
}
/* 主内容区布局 */
.main-content {
  display: flex;
  gap: 25px;
  padding: 5px;
}
.content-left {
  width: 68%;
}
.content-right {
  flex: 1;
  flex-shrink: 0;
}
/* 产品信息 */
.product-info {
  margin-bottom: 20px;
}
.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px 10px;
}
/* 悬浮框日期样式细化 */
.floating-date {
  color: #ff4d4f;
  font-weight: 500;
  white-space: nowrap; /* 防止日期换行 */
}
.info-item {
  padding: 8px 0;
  border-bottom: 1px dashed #f0f0f0;
  font-size: 14px;
}
.info-item:nth-child(3n + 1),
.info-item:nth-child(3n + 2),
.info-item:nth-child(3n) {
  border-bottom: 1px dashed #f0f0f0;
}
.info-item:last-child,
.info-item:nth-last-child(2),
.info-item:nth-last-child(3) {
  border-bottom: none;
}
.label {
  color: #666;
  margin-right: 5px;
}
.value {
  color: #333;
}
/* 服务承诺 */
.service-promises {
  margin-bottom: 20px;
}
.promise-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
/* 行程安排时间线样式 */
.schedule-timeline {
  padding: 20px;
}
.timeline-container {
  margin-top: 15px;
}
/* 天数标题 */
.day-title {
  margin: 30px 0 20px;
  position: relative;
}
.day-badge {
  background-color: #1677ff;
  color: white;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 16px;
  font-weight: 500;
}
/* 当天时间线容器 */
.day-timeline {
  position: relative;
  padding-left: 40px; /* 给时间轴留出空间 */
}
/* 垂直时间线 */
.timeline-line {
  position: absolute;
  left: 8px; /* 时间线位置 */
  top: 0;
  bottom: 0;
  width: 2px;
  background-color: #e5e7eb; /* 时间线颜色 */
}
/* 每个景点的时间线节点 */
.timeline-item {
  position: relative;
  margin-bottom: 30px;
}
.timeline-item:last-child {
  margin-bottom: 0;
}
/* 时间点标记 */
.timeline-dot {
  position: absolute;
  left: -40px; /* 相对于父容器左移，与时间线对齐 */
  top: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background-color: white;
  border: 2px solid #1677ff; /* 标记边框颜色 */
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2; /* 覆盖时间线 */
}
.timeline-dot i {
  color: #1677ff;
  font-size: 14px;
}
/* 时间与内容区域 */
.timeline-content {
  background-color: #f9fafb;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s;
}
.timeline-content:hover {
  transform: translateY(-3px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}
/* 景点时间 */
.spot-time {
  color: #1677ff;
  font-weight: 500;
  /* margin-bottom: 10px; */
  font-size: 14px;
  display: inline-block;
  padding: 3px 8px;
  background-color: rgba(22, 119, 255, 0.1);
  border-radius: 4px;
}
/* 景点内容 */
.spot-content {
  color: #333;
  line-height: 1.8;
}
.spot-content p {
  margin-bottom: 10px;
}
.spot-content p:last-child {
  margin-bottom: 0;
}
.spot-content img {
  width: 100%;
  max-width: 600px;
  height: 250px;
  object-fit: cover;
  border-radius: 6px;
  margin: 15px 0;
}
/* 路线详情 */
.detail-content {
  color: #333;
  line-height: 1.8;
}
.detail-content p {
  margin-bottom: 15px;
}
/* 费用说明 */
.cost-content {
  padding: 10px 0;
}
.cost-type {
  margin-bottom: 15px;
}
.cost-type:last-child {
  margin-bottom: 0;
}
.cost-type h4 {
  color: #1677ff;
  font-size: 16px;
  margin-bottom: 8px;
  font-weight: 500;
}
.cost-type p {
  color: #333;
  line-height: 1.6;
}
/* 预订须知 */
.notice-content {
  padding: 10px 0;
}
.notice-content p {
  color: #333;
  line-height: 1.6;
  margin-bottom: 10px;
}
.notice-content p:last-child {
  margin-bottom: 0;
}
/* 预订卡片 */
.booking-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 15px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  margin-bottom: 20px;
  position: sticky;
}
/* 价格区域 */
.price-section {
  background: linear-gradient(135deg, #f0f7ff 0%, #e6f4ff 100%);
  padding: 20px;
  border-bottom: 1px solid #e8e8e8;
}
.price-info {
  display: flex;
  align-items: baseline;
  margin-bottom: 10px;
}
.price {
  font-size: 32px;
  font-weight: bold;
  color: #ff4d4f;
}
.unit {
  font-size: 16px;
  color: #666;
  margin-left: 5px;
}
.rating {
  margin-top: 10px;
}
.stars {
  display: flex;
  align-items: center;
  margin-bottom: 5px;
}
.stars i {
  color: #faad14;
  margin-right: 2px;
}
.score {
  margin-left: 8px;
  color: #faad14;
  font-weight: 500;
}
.rating-text {
  font-size: 13px;
  color: #666;
}
/* 预订表单 */
.booking-form {
  padding: 20px;
}
.form-group {
  margin-bottom: 20px;
}
.form-label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
}
.form-row {
  display: flex;
  gap: 10px;
}
.half {
  flex: 1;
}
.booking-form .el-input-number {
  width: 100%;
}
.package-info {
  background-color: #f9f9f9;
  padding: 15px;
  border-radius: 6px;
  margin-bottom: 20px;
}
.package-info h4 {
  font-size: 15px;
  color: #333;
  margin-bottom: 10px;
  font-weight: 500;
}
.package-info .theme {
  display: flex;
  gap: 10px;
}
.package-info .tips .el-tag {
  /* color: #ff4d4f; */
  font-weight: bold;
}
.total-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-top: 1px dashed #e8e8e8;
  border-bottom: 1px dashed #e8e8e8;
  margin-bottom: 20px;
}
.total-label {
  font-size: 16px;
  color: #666;
}
.total-value {
  font-size: 24px;
  font-weight: bold;
  color: #ff4d4f;
}
.action-buttons {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 20px;
}
.book-btn {
  background-color: #ff4d4f;
  border-color: #ff4d4f;
  height: 46px;
  font-size: 16px;
  border-radius: 6px;
}
/* 游客评价 */
.reviews-card {
  margin-bottom: 20px;
}
.review-item {
  padding: 15px 0;
  border-bottom: 1px solid #f0f3f5;
}
.review-item:last-child {
  border-bottom: none;
}
.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.user-avatar img {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}
.user-info {
  flex: 1;
  margin-left: 10px;
}
.user-name {
  color: #333;
  font-weight: 500;
  margin-bottom: 3px;
}
.review-date {
  font-size: 12px;
  color: #999;
}
.review-stars {
  color: #faad14;
}
.review-stars .el-icon-star-on {
  font-size: large;
}
.review-content {
  color: #333;
  line-height: 1.6;
  font-size: 14px;
  margin-bottom: 10px;
}
.review-images {
  display: flex;
  gap: 8px;
}
.review-images img {
  width: 60px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
}
.view-more-reviews {
  width: 100%;
  color: #1677ff;
  padding: 10px 0;
  display: block;
  text-align: center;
}
/* 悬浮预订框 - 所有设备通用 */
.floating-booking {
  position: fixed;
  /* bottom: -100px; 默认隐藏在屏幕外 */
  left: 0;
  right: 0;
  background-color: #fff;
  padding: 30px 20px;
  box-shadow: 0 -2px 15px rgba(0, 0, 0, 0.1);
  z-index: 90; /* 确保在其他内容上方但不遮挡导航 */
  transition: all 0.3s ease;
}
.floating-booking.show {
  bottom: 0; /* 显示在屏幕底部 */
}
.floating-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
}
.floating-summary {
  flex: 1;
}
.floating-price {
  display: flex;
  align-items: center;
}
.floating-price span {
  padding-right: 30px;
  color: #ff4d4f;
  font-weight: bold;
  font-size: 24px;
}
.price-label {
  color: #666;
  margin-right: 5px;
}
.price-value {
  font-size: 22px;
  font-weight: bold;
  color: #ff4d4f;
}
.floating-desc {
  font-size: 13px;
  color: #666;
  margin-top: 4px;
}
.floating-book-btn {
  background-color: #ff4d4f;
  border-color: #ff4d4f;
  padding: 10px 30px;
  font-size: 16px;
  white-space: nowrap;
}
/* 行程安排中的图片 */
::v-deep .spot-content img {
  width: 100%;
  max-width: 200px; /* 限制最大宽度 */
  height: 200px; /* 固定高度 */
  object-fit: cover; /* 保持比例并裁剪多余部分 */
  border-radius: 4px;
  margin-right: 10px;
}
/* 路线详情中的图片 */
::v-deep .detail-content img {
  width: 100%;
  max-width: 800px;
  height: 400px;
  object-fit: cover;
  border-radius: 8px;
  margin: 20px auto;
  display: block;
}
.review-drawer-container {
  padding: 10px;
}
.show-review-btn {
  margin-bottom: 10px;
}
.review-list {
  padding: 10px 20px;
  max-height: calc(100vh - 120px); /* 根据实际布局调整高度 */
  overflow-y: auto;
  padding: 16px;
}
.loading-more,
.no-more {
  text-align: center;
  padding: 16px;
  color: #666;
  font-size: 14px;
}
.filter-tag {
  margin: 5px;
  cursor: pointer;
}
.review-item {
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eee;
}
.review-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}
.user-info {
  display: flex;
  flex-direction: column;
}
.user-name {
  font-weight: bold;
}
.review-date {
  font-size: 12px;
  color: #999;
}
.review-stars i {
  font-size: 14px;
}
.review-content {
  margin-bottom: 10px;
  line-height: 1.5;
}
.review-images {
  display: flex;
  flex-wrap: wrap;
}
.review-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
  margin-right: 10px;
  margin-bottom: 10px;
}
/* 日期选择弹窗样式 */
.date-selection {
  padding: 10px 0;
}
.available-dates {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}
.date-item {
  width: 80px;
  padding: 10px;
  text-align: center;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}
.date-item:hover {
  border-color: #1890ff;
}
.date-item.selected {
  background-color: #e6f7ff;
  border-color: #1890ff;
  color: #1890ff;
}
.date-day {
  margin-bottom: 5px;
  font-weight: 500;
}
.date-price {
  font-size: 12px;
  color: #f5222d;
}
.date-status {
  font-size: 12px;
  color: #faad14;
}
/* 手机端适配 */
@media (max-width: 992px) {
  .main-content {
    flex-direction: column;
  }
  .content-right,
  .content-left {
    width: 100%;
  }
  .booking-card {
    position: static;
    margin-top: 20px;
  }
  .floating-content {
    padding: 0 10px;
  }
  .floating-book-btn {
    padding: 8px 20px;
    font-size: 14px;
  }
}
@media (max-width: 768px) {
  .container {
    padding: 0 10px;
  }
  .floating-content {
    flex-wrap: wrap;
    gap: 10px;
  }
  .floating-item {
    margin-right: 10px;
    flex: 1 0 auto;
  }
  .spot-content img {
    height: 200px; /* 移动端行程图片高度减小 */
  }
  .detail-content img {
    height: 250px; /* 移动端详情图片高度减小 */
  }
  .route-title {
    font-size: 20px;
  }
  .el-carousel {
    height: 300px !important;
  }
  .thumbnail {
    width: 80px;
    height: 50px;
  }
  .info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .section {
    padding: 15px;
  }
  .card {
    padding: 0 10px;
  }
  .section-title {
    font-size: 16px;
    padding-bottom: 10px;
  }
  .action-buttons {
    flex-direction: column;
  }
  .book-btn {
    width: 100%;
  }
  .floating-desc {
    display: none; /* 移动端隐藏详细描述，节省空间 */
  }
  .floating-booking {
    padding: 20px;
  }
  .floating-content[data-v-cef8e8e8] {
    padding: 0px;
  }
  .price-value {
    font-size: 20px;
  }
}
@media (max-width: 480px) {
  .spot-content img {
    height: 160px;
  }
  .detail-content img {
    height: 200px;
  }
  .info-grid {
    grid-template-columns: 1fr;
  }
  /* .el-carousel {
    height: 180px !important;
  } */
  .thumbnail {
    width: 65px;
    height: 40px;
  }
  .tags {
    flex-wrap: wrap;
  }
  .price {
    font-size: 28px;
  }
  .total-value {
    font-size: 20px;
  }
}
</style>
